import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);


api.interceptors.response.use(
  (response) => response,
  (error) => {
    
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    
    if (error.response?.status === 401 && !isLoginRequest) {
      
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Auth
export const login = (username, password, remember_me = false) => 
  api.post('/auth/login', { username, password, remember_me });

// Dashboard
export const getDashboardStats = () => 
  api.get('/dashboard/stats');

export const getRecentOrders = (limit = 10) => 
  api.get(`/dashboard/recent-orders?limit=${limit}`);

// Orders
export const getOrders = (params = {}) => 
  api.get('/orders/', { params });

export const getOrder = (id) => 
  api.get(`/orders/${id}`);

export const createOrder = (data) => 
  api.post('/orders/', data);

export const updateOrder = (id, data) => 
  api.put(`/orders/${id}`, data);

export const deleteOrder = (id) => 
  api.delete(`/orders/${id}`);

export const exportOrdersCSV = (params = {}) => 
  api.get('/orders/export/csv', { params, responseType: 'blob' });

// Payments
export const createPayment = (data) => 
  api.post('/payments/', data);

export const getOrderPayments = (orderId) => 
  api.get(`/payments/order/${orderId}`);

export const deletePayment = (id) => 
  api.delete(`/payments/${id}`);

// Customers
export const getCustomers = (params = {}) => 
  api.get('/customers/', { params });

export const getCustomer = (id) => 
  api.get(`/customers/${id}`);

export const createCustomer = (data) => 
  api.post('/customers/', data);

export const updateCustomer = (id, data) => 
  api.put(`/customers/${id}`, data);

export const deleteCustomer = (id) => 
  api.delete(`/customers/${id}`);

export const searchCustomers = (q) => 
  api.get(`/customers/search?q=${q}`);

// Employees
export const getEmployees = (params = {}) => 
  api.get('/employees/', { params });

export const getEmployee = (id) => 
  api.get(`/employees/${id}`);

export const createEmployee = (data) => 
  api.post('/employees/', data);

export const updateEmployee = (id, data) => 
  api.put(`/employees/${id}`, data);

export const deleteEmployee = (id) => 
  api.delete(`/employees/${id}`);

export const terminateEmployee = (id, termination_date) => 
  api.post(`/employees/${id}/terminate`, null, { params: { termination_date } });

export const getEmployeeStats = () => 
  api.get('/employees/stats');

// Toplu çalışan silme
export const bulkDeleteEmployees = (ids) => 
  api.delete('/employees/bulk/delete', { data: ids });

// Leaves (İzinler) 
export const getEmployeeLeaves = (employeeId, year = null) => 
  api.get(`/leaves/employee/${employeeId}`, { params: year ? { year } : {} });

export const getAllLeaves = (params = {}) => 
  api.get('/leaves/', { params });

export const createEmployeeLeave = (data) => 
  api.post('/leaves/', data);

export const updateEmployeeLeave = (leaveId, data) => 
  api.put(`/leaves/${leaveId}`, data);

export const deleteEmployeeLeave = (leaveId) => 
  api.delete(`/leaves/${leaveId}`);

export const getEmployeeLeaveStats = (employeeId, year = null) => 
  api.get(`/leaves/stats/employee/${employeeId}`, { params: year ? { year } : {} });

// Users
export const getUsers = () => 
  api.get('/users/');

export const getCurrentUser = () => 
  api.get('/users/me');

export const createUser = (data) => 
  api.post('/users/', data);

export const updateUser = (id, data) => 
  api.put(`/users/${id}`, data);

export const deleteUser = (id) => 
  api.delete(`/users/${id}`);

export const changePassword = (data) => 
  api.post('/users/change-password', data);

// Roles & Permissions
export const getPermissions = () => 
  api.get('/roles/permissions');

export const seedPermissions = () => 
  api.post('/roles/permissions/seed');

export const getRoles = () => 
  api.get('/roles/');

export const createRole = (data) => 
  api.post('/roles/', data);

export const updateRole = (id, data) => 
  api.put(`/roles/${id}`, data);

export const deleteRole = (id) => 
  api.delete(`/roles/${id}`);

export const assignRoles = (data) => 
  api.post('/roles/assign', data);

export const getUserPermissions = (userId) => 
  api.get(`/roles/user/${userId}/permissions`);

export const getMyPermissions = () => 
  api.get('/roles/my-permissions');

// Reports
export const getMonthlyRevenue = (year) => 
  api.get(`/reports/monthly-revenue?year=${year}`);

export const getTopCustomers = (params = {}) => 
  api.get('/reports/top-customers', { params });

export const getPaymentStatus = () => 
  api.get('/reports/payment-status');

export const getPerformanceMetrics = (days = 30) => 
  api.get(`/reports/performance?days=${days}`);

// Password Reset Requests
export const createPasswordResetRequest = (data) => 
  api.post('/password-reset/request', data);

export const getPasswordResetRequests = (status = null) => 
  api.get('/password-reset/requests', { params: status ? { status } : {} });

export const getPendingResetCount = () => 
  api.get('/password-reset/requests/pending/count');

export const processPasswordResetRequest = (requestId, data) => 
  api.post(`/password-reset/requests/${requestId}/process`, data);

export const deletePasswordResetRequest = (requestId) => 
  api.delete(`/password-reset/requests/${requestId}`);

// Vehicles (Araçlar)
export const getVehicles = (params = {}) => 
  api.get('/vehicles/', { params });

export const getVehicle = (id) => 
  api.get(`/vehicles/${id}`);

export const createVehicle = (data) => 
  api.post('/vehicles/', data);

export const updateVehicle = (id, data) => 
  api.put(`/vehicles/${id}`, data);

export const deleteVehicle = (id) => 
  api.delete(`/vehicles/${id}`);

export const getVehicleWarnings = () => 
  api.get('/vehicles/warnings');

export const getVehicleStats = () => 
  api.get('/vehicles/stats');

export const bulkDeleteVehicles = (ids) => 
  api.delete('/vehicles/bulk/delete', { data: ids });

// Vehicle Maintenances
export const getVehicleMaintenances = (vehicleId) => 
  api.get(`/vehicles/${vehicleId}/maintenances`);

export const getAllMaintenances = (params = {}) => 
  api.get('/vehicles/maintenances/all', { params });

export const createMaintenance = (data) => 
  api.post('/vehicles/maintenances', data);

export const updateMaintenance = (id, data) => 
  api.put(`/vehicles/maintenances/${id}`, data);

export const deleteMaintenance = (id) => 
  api.delete(`/vehicles/maintenances/${id}`);

// ============================================================
// frontend/src/services/api.js dosyasına EKLENECEK fonksiyonlar
// ============================================================

// ==================== CASH FLOW (Kasa İşlemleri) ====================

// Cash Accounts (Kasa/Hesap)
export const getCashAccounts = (params = {}) => 
  api.get('/cashflow/accounts', { params });

export const createCashAccount = (data) => 
  api.post('/cashflow/accounts', data);

export const updateCashAccount = (id, data) => 
  api.put(`/cashflow/accounts/${id}`, data);

export const deleteCashAccount = (id) => 
  api.delete(`/cashflow/accounts/${id}`);

// Expense Categories (Gider Kategorileri)
export const getExpenseCategories = (params = {}) => 
  api.get('/cashflow/categories', { params });

export const createExpenseCategory = (data) => 
  api.post('/cashflow/categories', data);

export const updateExpenseCategory = (id, data) => 
  api.put(`/cashflow/categories/${id}`, data);

export const deleteExpenseCategory = (id) => 
  api.delete(`/cashflow/categories/${id}`);

// Cash Transactions (Kasa İşlemleri)
export const getCashTransactions = (params = {}) => 
  api.get('/cashflow/transactions', { params });

export const getCashTransaction = (id) => 
  api.get(`/cashflow/transactions/${id}`);

export const createCashTransaction = (data) => 
  api.post('/cashflow/transactions', data);

export const updateCashTransaction = (id, data) => 
  api.put(`/cashflow/transactions/${id}`, data);

export const deleteCashTransaction = (id) => 
  api.delete(`/cashflow/transactions/${id}`);

// Cash Reports
export const getCashDailyReport = (params = {}) => 
  api.get('/cashflow/reports/daily', { params });

export const getCashMonthlyReport = (params = {}) => 
  api.get('/cashflow/reports/monthly', { params });

export const getCashCategoryReport = (params = {}) => 
  api.get('/cashflow/reports/by-category', { params });

export const getCashPaymentMethodReport = (params = {}) => 
  api.get('/cashflow/reports/by-payment-method', { params });


// ==================== SUPPLIERS (Tedarikçiler) ====================

// Suppliers
export const getSuppliers = (params = {}) => 
  api.get('/suppliers/', { params });

export const getSupplier = (id) => 
  api.get(`/suppliers/${id}`);

export const createSupplier = (data) => 
  api.post('/suppliers/', data);

export const updateSupplier = (id, data) => 
  api.put(`/suppliers/${id}`, data);

export const deleteSupplier = (id) => 
  api.delete(`/suppliers/${id}`);

// Supplier Invoices (Faturalar)
export const getSupplierInvoices = (supplierId, params = {}) => 
  api.get(`/suppliers/${supplierId}/invoices`, { params });

export const getAllSupplierInvoices = (params = {}) => 
  api.get('/suppliers/invoices/all', { params });

export const createSupplierInvoice = (data) => 
  api.post('/suppliers/invoices', data);

export const updateSupplierInvoice = (id, data) => 
  api.put(`/suppliers/invoices/${id}`, data);

export const deleteSupplierInvoice = (id) => 
  api.delete(`/suppliers/invoices/${id}`);

// Supplier Payments (Ödemeler)
export const getSupplierPayments = (supplierId, params = {}) => 
  api.get(`/suppliers/${supplierId}/payments`, { params });

export const getAllSupplierPayments = (params = {}) => 
  api.get('/suppliers/payments/all', { params });

export const createSupplierPayment = (data) => 
  api.post('/suppliers/payments', data);

export const updateSupplierPayment = (id, data) => 
  api.put(`/suppliers/payments/${id}`, data);

export const deleteSupplierPayment = (id) => 
  api.delete(`/suppliers/payments/${id}`);

// Supplier Ledger & Reports
export const getSupplierLedger = (supplierId, params = {}) => 
  api.get(`/suppliers/${supplierId}/ledger`, { params });

export const getSuppliersSummary = (params = {}) => 
  api.get('/suppliers/reports/summary', { params });

export const getPaymentTransfers = (params) => 
  api.get('/cashflow/transfers', { params });

export const createPaymentTransfer = (data) => 
  api.post('/cashflow/transfers', data);

export const updatePaymentTransfer = (id, data) => 
  api.put(`/cashflow/transfers/${id}`, data);

export const deletePaymentTransfer = (id) => 
  api.delete(`/cashflow/transfers/${id}`);

export const bulkDeleteOrders = (ids) =>
  api.delete('/orders/bulk/delete', { data: ids });

export const bulkDeleteCustomers = (ids) =>
  api.delete('/customers/bulk/delete', { data: ids });

export const getRepresentatives = () =>
  api.get('/representatives/');

export const createRepresentative = (name) =>
  api.post('/representatives/', { name });

export const deleteRepresentative = (id) =>
  api.delete(`/representatives/${id}`);

export default api;